export type Direction = 'positive' | 'negative' | 'balanced'

export enum VisualizationType {
  QUOTE = 'quote',
  NUMBER = 'number',
  PERCENT = 'percent',
  CHART = 'chart',
  EMOTION = 'emotion',
  BAR_CHART = 'barChart',
  STAR_CHART = 'star_chart',
  TEAM_HEALTH_HEART = 'team_health_heart',
  PART_OF_WHOLE = 'part_of_whole',
  COMPARISON_BAR_CHART = 'comparison_bar_chart',
  STACKED_LINE_CHART_CENTERED = 'stacked_line_chart_centered'
}

export enum EmotionType {
  HAPPY = 'happy',
  SAD = 'sad',
  ANGRY = 'angry',
  STRESSED = 'stressed',
  ANXIOUS = 'anxious',
  EXCITED = 'excited',
  CALM = 'calm',
  BORED = 'bored',
  CONTENT = 'content',
  DISAPPOINTED = 'disappointed',
  EMBARRASSED = 'embarrassed',
  IRRITATED = 'irritated',
  UPSET = 'upset',
  WORRIED = 'worried'
}

export interface VisualizationQuote {
  visualizationType: 'quote'
  visualizationValue: string
}

export interface VisualizationNumber {
  visualizationType: 'number'
  visualizationValue: string
}

export interface VisualizationEmotion {
  visualizationType: 'emotion'
  visualizationValue: string
}

export interface VisualizationChart {
  visualizationType: 'chart' | 'barChart'
  visualizationValue: {
    prevValue: number
    newValue: number
  }
}

export interface VisualizationStarChart {
  visualizationType: 'star_chart'
  visualizationValue: {
    values: number[]
  }
}

export interface VisualizationTeamHealthHeart {
  visualizationType: 'team_health_heart'
  visualizationValue: {
    value: number
  }
}

export interface VisualizationPartOfWhole {
  visualizationType: 'part_of_whole'
  visualizationValue: {
    value: number
  }
}

export interface VisualizationComparisonBarChart {
  visualizationType: 'comparison_bar_chart'
  visualizationValue:
    | {
        current: number
        previous?: number
      }
    | Array<{
        label: string
        value: number
      }>
}

export interface VisualizationStackedLineChartCentered {
  visualizationType: 'stacked_line_chart_centered'
  visualizationValue: {
    loops: Array<{
      loopIndex: number
      players: Array<{
        playerId: string
        displayName: string
        talkedTotal: number
      }>
    }>
  }
}

export type Visualization =
  | VisualizationQuote
  | VisualizationNumber
  | VisualizationEmotion
  | VisualizationChart
  | VisualizationStarChart
  | VisualizationTeamHealthHeart
  | VisualizationPartOfWhole
  | VisualizationComparisonBarChart
  | VisualizationStackedLineChartCentered

export interface MetricBase {
  value: number
  isTeamRelevant?: boolean
  direction?: Direction
}

export interface MetricData {
  [metricName: string]: MetricBase
}

// Single tab data structure
export interface TabData {
  [metricName: string]: MetricBase | Visualization
  visualization: Visualization
}

// Section with points of interest array
export interface SectionWithPointsOfInterest {
  pointsOfInterest: TabData[]
}

// --- TEAM STRUCTURE ---
export interface TeamData {
  profileData: {
    pathSelected: MetricBase
    timeParalyzed: MetricBase
    inactiveTimeOutside: MetricBase
  }

  planningExecutingEvaluating: SectionWithPointsOfInterest
  teamPsychologicalSafety: SectionWithPointsOfInterest
  taskPerformance: SectionWithPointsOfInterest
  communicationClimate: SectionWithPointsOfInterest
  improvementAreas: SectionWithPointsOfInterest
  strengths: SectionWithPointsOfInterest
}

// --- INDIVIDUAL STRUCTURE ---
export interface IndividualData {
  sessionProfile: {
    pathSelected: MetricBase
    timeParalyzed: MetricBase
  }

  experience: SectionWithPointsOfInterest
  improvementAreas: SectionWithPointsOfInterest
  strengths: SectionWithPointsOfInterest
}

// --- ROOT ---
export interface DataPoints {
  datapoints: {
    teamData: TeamData
    individual: IndividualData
  }
}
